import React from 'react';
import { Link } from 'react-router-dom';

export const LEGAL_CONTACT_EMAIL = 'standlogicapp@gmail.com';

export function TermsOfService() {
  return (
    <div className="support-card">
      <h3>Terms of Service</h3>
      <p className="support-intro" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>Last updated: October 2026</p>
      <p className="support-intro">
        These terms govern your use of StandLogic, including the website and the Android app. By creating an account or using StandLogic, you agree to them. If you don't agree, please don't use the service.
      </p>

      <div className="support-section">
        <h4>What StandLogic Is</h4>
        <p>
          StandLogic provides hunting-condition forecasts and scores for whitetail deer, based on public weather data, lunar calculations and published research on deer behavior. It is an informational tool to help you plan. It is not professional, legal, safety or wildlife-management advice. Please read the <Link to="/disclaimer">Disclaimer</Link>, which is part of these terms.
        </p>
      </div>

      <div className="support-section">
        <h4>Your Account</h4>
        <ul className="support-list">
          <li>You sign in with Google. You're responsible for activity under your account and for keeping your Google account secure.</li>
          <li>You must be at least 13 years old to use StandLogic. If you are under the age of majority where you live, you need a parent or guardian's permission.</li>
          <li>Provide accurate information, and don't create accounts for anyone else without their permission.</li>
          <li>You can ask us to delete your account at any time. See the <Link to="/privacy">Privacy Policy</Link>.</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>Acceptable Use</h4>
        <p>You agree not to:</p>
        <ul className="support-list">
          <li>Use automated tools, scripts or bots to access the service in bulk, scrape data, or get around rate limits and plan limits.</li>
          <li>Interfere with or overload the service, probe it for vulnerabilities, or try to access accounts or data that aren't yours.</li>
          <li>Resell, redistribute or republish StandLogic scores or forecasts as a competing product or service.</li>
          <li>Use StandLogic to break any law, including hunting, trespass and wildlife laws.</li>
          <li>Copy, reverse engineer or extract the scoring logic, except as the law allows.</li>
        </ul>
        <p>
          To keep the service available for everyone, we apply request limits. If you exceed them you may be temporarily blocked from making further requests.
        </p>
      </div>

      <div className="support-section">
        <h4>Free and Plus Plans</h4>
        <p>
          StandLogic offers a Free plan and a Plus plan with different features and forecast ranges, as described on the Support page. Plan features can change. Plus is a subscription sold through Google Play in the StandLogic Android app, billed monthly or annually at the price shown before you buy. Subscriptions renew automatically until canceled. You can cancel anytime in Google Play, and you keep Plus through the end of the paid period. Purchases, renewals and refunds are handled by Google Play under its terms and policies.
        </p>
      </div>

      <div className="support-section">
        <h4>Third-Party Data and Services</h4>
        <p>
          StandLogic relies on third parties, including Google (sign-in and Google Play billing), Amazon Web Services (hosting), WeatherAPI.com and Open-Meteo (weather data) and Zippopotam.us (zipcode lookup). We don't control them. Their outages, errors or changes can affect what you see, and we aren't responsible for their content or availability.
        </p>
      </div>

      <div className="support-section">
        <h4>Availability and Changes</h4>
        <p>
          We work to keep StandLogic running but don't promise uninterrupted or error-free service. We may change, suspend or discontinue any part of the service, and update how scores are calculated, at any time.
        </p>
      </div>

      <div className="support-section">
        <h4>Intellectual Property</h4>
        <p>
          StandLogic, including its software, scoring logic, design, text and branding, belongs to us or our licensors. You get a personal, non-exclusive, non-transferable, revocable right to use the service for your own non-commercial hunting planning. Weather and astronomical source data belong to their respective providers and are subject to their terms.
        </p>
      </div>

      <div className="support-section">
        <h4>Termination</h4>
        <p>
          You can stop using StandLogic at any time. We may suspend or end your access if you break these terms, abuse the service, or put it or other users at risk. We may also end the service entirely.
        </p>
      </div>

      <div className="support-section">
        <h4>No Warranties</h4>
        <p>
          StandLogic is provided "as is" and "as available," without warranties of any kind, whether express or implied, including accuracy, reliability, fitness for a particular purpose, and non-infringement. Scores and forecasts are estimates and can be wrong. Some places don't allow certain warranty exclusions, so parts of this section may not apply to you.
        </p>
      </div>

      <div className="support-section">
        <h4>Limitation of Liability</h4>
        <p>
          To the fullest extent the law allows, StandLogic and its owner will not be liable for any indirect, incidental, special, consequential or punitive damages, or for lost opportunities, lost hunting time, travel or equipment costs, or any injury, loss or damage arising from hunting or other outdoor activity, whether or not you relied on StandLogic. Our total liability for any claim relating to the service is limited to the greater of the amount you paid us in the 12 months before the claim or US $50. Some places don't allow these limits, so they may not fully apply to you.
        </p>
      </div>

      <div className="support-section">
        <h4>Your Responsibility</h4>
        <p>
          You are solely responsible for your own safety and conduct, for following all hunting laws and land-access rules, and for decisions you make in the field. You agree to hold StandLogic and its owner harmless from claims arising from your use of the service in violation of these terms or the law.
        </p>
      </div>

      <div className="support-section">
        <h4>Governing Law and Disputes</h4>
        <p>
          These terms are governed by the laws of the State of Maryland, without regard to its conflict-of-law rules. Any dispute relating to these terms or the service will be brought in the state or federal courts located in Maryland, and you and we consent to the jurisdiction of those courts. Nothing here limits any rights you have under mandatory consumer protection laws where you live.
        </p>
      </div>

      <div className="support-section">
        <h4>Changes to These Terms</h4>
        <p>
          We may update these terms. When we do, we'll change the date above, and for significant changes we'll make a reasonable effort to notify you in the app. Continuing to use StandLogic after a change means you accept the updated terms.
        </p>
      </div>

      <div className="support-section">
        <h4>Contact</h4>
        <p>
          Questions about these terms: <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}

export function Disclaimer() {
  return (
    <div className="support-card">
      <h3>Disclaimer</h3>
      <p className="support-intro" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>Last updated: October 2026</p>
      <p className="support-intro">
        StandLogic helps you plan. It does not decide for you, and it can't promise what you'll see in the woods. Please read this before relying on any score.
      </p>

      <div className="support-section">
        <h4>Not a Guarantee</h4>
        <p>
          Scores and star ratings are estimates built from public weather forecasts, lunar data and published research. Deer behavior depends on many things no model captures, including hunting pressure, food sources, terrain, human activity and individual animals. A high score does not mean you will see a deer, and a low score does not mean you won't.
        </p>
      </div>

      <div className="support-section">
        <h4>Forecasts Can Be Wrong</h4>
        <p>
          Weather data comes from third parties and forecasts change, especially several days out. Zipcode-based coordinates are approximate, so conditions at your stand may differ from those used for your score. Sunrise and sunset times are calculated estimates; always confirm legal shooting hours for your area. Days beyond the weather forecast window are scored on moon phase alone and are less reliable.
        </p>
      </div>

      <div className="support-section">
        <h4>Follow the Law</h4>
        <p>
          Hunting seasons, bag limits, legal hours, permitted methods, licensing and land-access rules vary by state, county and property, and they change. StandLogic does not provide or verify any of this information. You are responsible for knowing and following all local, state and federal laws and landowner rules. Check your state wildlife agency for current regulations.
        </p>
      </div>

      <div className="support-section">
        <h4>Safety Comes First</h4>
        <p>
          Nothing in StandLogic is safety advice. Hunting and being outdoors carry real risks, including firearms and archery equipment, falls from tree stands, severe weather, cold exposure, and getting lost. Use a safety harness, tell someone where you'll be, carry appropriate gear, and make your own judgment about conditions. Don't go out, or stay out, because a score says so. Severe weather forecasts in the app are not a substitute for official weather warnings.
        </p>
      </div>

      <div className="support-section">
        <h4>Your Decisions, Your Responsibility</h4>
        <p>
          You assume all risk for decisions you make using StandLogic. StandLogic and its owner are not liable for any injury, loss, damage, missed opportunity or legal consequence that results from using the service. See the <Link to="/terms">Terms of Service</Link> for the full terms.
        </p>
      </div>

      <div className="support-section">
        <h4>Not Affiliated</h4>
        <p>
          StandLogic is an independent product and is not affiliated with, endorsed by, or sponsored by any government wildlife agency, hunting organization, or weather or data provider.
        </p>
      </div>

      <div className="support-section">
        <h4>Contact</h4>
        <p>
          Questions: <a href={`mailto:${LEGAL_CONTACT_EMAIL}`}>{LEGAL_CONTACT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}
